import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../context/ApiContext";

export default function RoomTypesAdmin() {
  const navigate = useNavigate();
  const { adminListRoomTypes, adminDeleteRoomType, adminBulkImportRoomTypes } = useApi();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [pasteValue, setPasteValue] = useState("[\n  { \"name\": \"Studio\", \"beds\": 0, \"baths\": 1, \"sqft\": 25, \"basePrice\": 0 }\n]");
  const fileInputRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const list = await adminListRoomTypes();
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || "Failed to load room types");
    } finally {
      setLoading(false);
    }
  }, [adminListRoomTypes]);

  useEffect(() => { refresh(); }, [refresh]);

  async function onDelete(id) {
    if (!window.confirm("Delete this room type?")) return;
    await adminDeleteRoomType(id);
    await refresh();
  }

  function onExport() {
    try {
      const data = JSON.stringify(items || [], null, 2);
      const blob = new Blob([data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `room-types-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Failed to export room types: " + (e?.message || e));
    }
  }

  function onImportClick() {
    fileInputRef.current?.click();
  }

  function onImportFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || "[]"));
        if (!Array.isArray(parsed)) throw new Error("Invalid file format");
        adminBulkImportRoomTypes(parsed).then(() => refresh());
        alert(`Imported ${parsed.length} item(s).`);
      } catch (err) {
        alert("Failed to import: " + (err?.message || err));
      } finally {
        e.target.value = ""; // reset file input
      }
    };
    reader.onerror = () => {
      alert("Could not read file");
      e.target.value = "";
    };
    reader.readAsText(file);
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold">Room Types</h1>
        <div className="flex items-center gap-2">
          <button onClick={onExport} className="px-3 py-2 rounded-md border">Export</button>
          <button onClick={onImportClick} className="px-3 py-2 rounded-md border">Import</button>
          <button onClick={() => setShowPaste((s) => !s)} className="px-3 py-2 rounded-md border">{showPaste ? "Close Paste" : "Paste JSON"}</button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={onImportFile}
          />
          <button onClick={() => navigate("/admin/room-types/new")} className="px-3 py-2 rounded-md bg-black text-white">New Room Type</button>
        </div>
      </div>

      {showPaste && (
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold">Paste JSON</h2>
            <button
              className="px-3 py-1.5 rounded-md bg-black text-white"
              onClick={async () => {
                try {
                  const arr = JSON.parse(pasteValue);
                  if (!Array.isArray(arr)) throw new Error("Expected an array");
                  await adminBulkImportRoomTypes(arr);
                  await refresh();
                  alert(`Imported ${arr.length} item(s).`);
                } catch (e) {
                  alert(e?.message || String(e));
                }
              }}
            >
              Import
            </button>
          </div>
          <textarea
            className="w-full h-40 border rounded-md px-3 py-2 font-mono text-sm"
            value={pasteValue}
            onChange={(e) => setPasteValue(e.target.value)}
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-md p-3 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="text-gray-600">Loading...</div>
      ) : null}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <h2 className="font-semibold mb-3">Saved Types</h2>
          {items.length === 0 ? (
            <div className="text-gray-500">No room types yet.</div>
          ) : (
            <ul className="divide-y">
              {items.map((it) => (
                <li key={it.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-gray-600">
                      {it.beds} bed • {it.baths} bath • {it.sqft} sqm • R{Number(it.basePrice || 0).toLocaleString()}
                    </div>
                    {Array.isArray(it.charges) && it.charges.length > 0 && (
                      <div className="text-xs text-gray-600">
                        Charges: {it.charges.map((c) => `${c.name}: R${Number(c.amount||0).toLocaleString()}`).join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => navigate(`/admin/room-types/${it.id}/edit`)} className="px-2 py-1 rounded-md border">Edit</button>
                    <button onClick={() => onDelete(it.id)} className="px-2 py-1 rounded-md border text-red-600">Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
